import { Link } from "react-router-dom";
import { Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Calendar, ChevronDown, ChevronUp, List, Package, Plus, Truck } from "lucide-react";
import { useMemo, useState } from "react";
import { useGetMyBookings } from "~/services/booking";
import { useGetMyInventories } from "~/services/inventory";
import { useAuth } from "~/store";
import { TBooking, TClient, TServiceTicket } from "~/types";
import { CheckType, InventoryStatusCode, JobStatus, ServiceType } from "~/utils/constants";
import { RESPONSIVE_CLASSES } from "~/utils/responsive";
import { capitalize } from "~/utils";
import dayjs from "dayjs";
import { RequestServiceSidebar } from "./overview/request-service-sidebar";
import { ServicesCalendarView } from "./overview/services-calendar-view";
import { ServiceDetailSidebar } from "~/components/client/bookings/service-detail-sidebar";

const { Text } = Typography;

interface ServiceRow {
  id: string;
  serviceDatetime: string;
  serial: string;
  project: string;
  serviceType: ServiceType;
  bookingRef: string;
  location: string;
  status: JobStatus;
  rawBooking: TBooking;
  rawService: TServiceTicket;
}

const getServiceTypeIcon = (type: ServiceType) => {
  switch (type) {
    case ServiceType.DELIVERY:
      return <Truck size={14} />;
    case ServiceType.COLLECTION:
      return <Package size={14} />;
    default:
      return <Calendar size={14} />;
  }
};

const getStatusBadge = (status: JobStatus) => {
  // In Progress is special - just italic black text, no box
  if (status === JobStatus.IN_PROGRESS) {
    return (
      <span className="text-[#1a1a2e] italic font-bold text-xs uppercase">
        In Progress
      </span>
    );
  }
  
  // Scheduled is special - just black text, no box, no italics
  if (status === JobStatus.SCHEDULED) {
    return (
      <span className="text-[#1a1a2e] font-bold text-xs uppercase">
        Scheduled
      </span>
    );
  }
  
  const statusConfig = {
    [JobStatus.DRAFT]: { text: "Draft", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
    [JobStatus.COMPLETED]: { text: "Completed", className: "bg-[#1a1a2e] text-white", extraClass: "" },
    [JobStatus.CANCELLED]: { text: "Cancelled", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
    [JobStatus.ESTIMATED]: { text: "Estimated", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
  };
  const config = statusConfig[status] || { text: status, className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" };
  return (
    <span className={`inline-block px-2 py-1 text-xs font-bold uppercase border border-[#e5e5e5] ${config.className}`}>
      {config.text}
    </span>
  );
};

export function Overview() {
  const { user } = useAuth();
  const client: TClient | null | undefined = user?.client;
  const currentClientId = user?.client?.id;
  const [isServiceSidebarOpen, setIsServiceSidebarOpen] = useState(false);
  const [servicesViewType, setServicesViewType] = useState<"list" | "calendar">("list");
  const [selectedService, setSelectedService] = useState<TServiceTicket | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<TBooking | null>(null);
  const [isShippingAddressesExpanded, setIsShippingAddressesExpanded] = useState(false);
  
  const { data: inventory, isLoading: inventoryLoading } = useGetMyInventories({
    checkParentId: CheckType.UNCHECK,
    pageSize: 500, // Max allowed by backend validation
  });
  const { data: storageInventory, isLoading: storageLoading } = useGetMyInventories({
    checkParentId: CheckType.UNCHECK,
    status: InventoryStatusCode.AVAILABLE_ON_HAND,
  });

  // Calculate subclient inventory count
  const subclientInventoryCount = useMemo(() => {
    if (!inventory?.items) return 0;
    return inventory.items.filter((item) => item.client?.isSubClient === true).length;
  }, [inventory?.items]);
  
  // Get all bookings including estimates
  const { data: myBookings, isLoading: isLoadingBookings } = useGetMyBookings({
    page: 1,
    pageSize: 100,
  });

  // Flatten all services from all bookings and filter for scheduled services where user is booking client
  const upcomingServices = useMemo(() => {
    if (!myBookings?.items || !currentClientId) return [];

    const allServices: ServiceRow[] = myBookings.items.flatMap((booking) => {
      const serviceTickets = booking.serviceTicket || [];
      return serviceTickets.map((service: TServiceTicket): ServiceRow => {
        const projectName = booking.project?.name;
        const sidemark = booking.sidemark;
        let projectDisplay = '-';
        if (projectName && sidemark) {
          projectDisplay = `${projectName} / ${sidemark}`;
        } else if (projectName) {
          projectDisplay = projectName;
        } else if (sidemark) {
          projectDisplay = sidemark;
        }

        return {
          id: service.id,
          serviceDatetime: service.serviceDatetime ? (typeof service.serviceDatetime === 'string' ? service.serviceDatetime : new Date(service.serviceDatetime).toISOString()) : '',
          serial: service.serial,
          project: projectDisplay,
          serviceType: service.serviceType,
          bookingRef: booking.referenceNumber,
          location: service.location
            ? `${service.location.city || ""}${service.location.city && service.location.state ? ", " : ""}${service.location.state || ""}`.trim() || "-"
            : "-",
          status: service.status,
          rawBooking: booking,
          rawService: service,
        };
      });
    });

    // Filter for scheduled services where user is the booking client (not service client)
    return allServices.filter((service) => {
      // Only show services where the booking client matches the current client
      const isBookingClient = service.rawBooking.clientId === currentClientId;
      // Only show SCHEDULED services
      const isScheduled = service.status === JobStatus.SCHEDULED;
      return isBookingClient && isScheduled;
    }).sort((a, b) => {
      const dateA = a.serviceDatetime ? new Date(a.serviceDatetime).getTime() : 0;
      const dateB = b.serviceDatetime ? new Date(b.serviceDatetime).getTime() : 0;
      return dateA - dateB;
    });
  }, [myBookings, currentClientId]);

  const handleServiceRowClick = (record: ServiceRow) => {
    setSelectedService(record.rawService);
    setSelectedBooking(record.rawBooking);
  };

  const handleCloseServiceSidebar = () => {
    setSelectedService(null);
    setSelectedBooking(null);
  };

  const isServiceDetailSidebarOpen = selectedService !== null;

  const serviceColumns: ColumnsType<ServiceRow> = [
    {
      title: "Date",
      dataIndex: "serviceDatetime",
      key: "serviceDatetime",
      width: 150,
      render: (date: string) => (date ? dayjs(date).format('MMM D, YYYY') : " - "),
    },
    {
      title: "Service Ref",
      dataIndex: "serial",
      key: "serial",
      width: 120,
      render: (text: string) => <Text strong>{text || " - "}</Text>,
    },
    {
      title: "Project / Sidemark",
      dataIndex: "project",
      key: "project",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "serviceType",
      key: "serviceType",
      width: 120,
      render: (text: ServiceType) => (
        <div className="flex items-center gap-1">
          {getServiceTypeIcon(text)}
          <span>{text ? capitalize(text) : " - "}</span>
        </div>
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 150,
      ellipsis: true,
      render: (text: string) => text || " - ",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: JobStatus) => getStatusBadge(status),
    },
  ];

  if (!client) {
    return (
      <div className={RESPONSIVE_CLASSES.container}>
        <h2
          className={`${RESPONSIVE_CLASSES.titleText} font-serif font-bold text-neutral-900 ${RESPONSIVE_CLASSES.marginBottom}`}
        >
          Account Overview
        </h2>
        <p className={`${RESPONSIVE_CLASSES.bodyText} text-neutral-600`}>No client data available.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full relative bg-[#f2f2f2]">
      {/* Main Content */}
      <div
        className={`flex-1 transition-all duration-300 ease-in-out ${
          isServiceSidebarOpen ? "lg:mr-[420px]" : ""
        }`}
      >
        <div className={`${RESPONSIVE_CLASSES.container} bg-[#f2f2f2]`}>
          <div className={RESPONSIVE_CLASSES.marginBottom}>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1a2e] uppercase tracking-wide !mb-1">
              Account Overview
            </h2>
            <p className="text-[#666] font-medium">
              Manage your services, inventory, and account information
            </p>
          </div>

          <div className="space-y-6">
        {/* Company Information and Action Buttons - Side by Side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {/* Left Side - Company Information */}
          <div className="border border-[#e5e5e5] rounded-lg shadow-sm bg-white p-4 sm:p-6 flex flex-col h-full">
            <div className="mb-4">
              <h3 className="font-bold text-sm uppercase tracking-wide text-[#1a1a2e] !mb-0">
                Company Information
              </h3>
            </div>
            <div className="flex flex-col flex-1 justify-between">
              <div className="space-y-4">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide mb-1 text-[#1a1a2e]">Company Name</div>
                  <div className="font-medium text-[#1a1a2e]">{client.companyName || " - "}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide mb-1 text-[#1a1a2e]">Primary Contact</div>
                  <div className="font-medium text-[#1a1a2e]">{`${client.firstName} ${client.lastName}`}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide mb-1 text-[#1a1a2e]">Email</div>
                  <div className="font-medium text-[#1a1a2e] break-all">{client.email || " - "}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide mb-1 text-[#1a1a2e]">Contact Phone</div>
                  <div className="font-medium text-[#1a1a2e]">{client.contactPhone || " - "}</div>
                </div>
              </div>
                
              {/* Billing Address */}
              <div className="pt-4 mt-4 border-t border-[#e5e5e5]">
              <div className="text-xs font-bold uppercase tracking-wide mb-2 text-[#1a1a2e]">Billing Address</div>
              {client.billingAddress ? (
                <div>
                  <div className="font-bold text-sm text-[#1a1a2e] mb-1">
                    {client.billingAddress.addressName || "Billing Address"}
                  </div>
                  <div className="text-sm font-medium text-[#1a1a2e]">
                    {client.billingAddress.address}
                  </div>
                  {client.billingAddress.anotherAddress && (
                    <div className="text-sm font-medium text-[#1a1a2e]">
                      {client.billingAddress.anotherAddress}
                    </div>
                  )}
                  <div className="text-sm font-medium text-[#1a1a2e]">
                    {client.billingAddress.city}, {client.billingAddress.state} {client.billingAddress.zip}
                  </div>
                  {client.billingAddress.country && (
                    <div className="text-sm font-medium text-[#1a1a2e]">
                      {client.billingAddress.country}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm font-medium text-[#1a1a2e]">
                  No billing address available.
                </div>
              )}
              </div>
            </div>
          </div>

          {/* Right Side - Action Buttons */}
          <div className="border border-[#e5e5e5] rounded-lg shadow-sm bg-white p-4 sm:p-6 flex flex-col h-full">
            <div className="mb-4">
              <h3 className="font-bold text-sm uppercase tracking-wide text-[#1a1a2e] !mb-0">
                Quick Actions
              </h3>
            </div>
            <div className="flex flex-col flex-1 justify-between">
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => setIsServiceSidebarOpen(true)}
                  className="border border-[#1a1a2e] rounded-lg bg-[#1a1a2e] text-white px-4 py-3 font-bold text-sm uppercase tracking-wide hover:opacity-90 transition-all w-full text-left flex items-center gap-2"
                >
                  <Plus size={18} />
                  Request Service
                </button>
                <Link
                  to="/inventory"
                  className="border border-[#e5e5e5] rounded-lg bg-white text-[#1a1a2e] px-4 py-3 font-bold text-sm uppercase tracking-wide hover:bg-[#fafafa] transition-all text-center"
                >
                  View Inventory
                </Link>
                <Link
                  to="/job"
                  className="border border-[#e5e5e5] rounded-lg bg-white text-[#1a1a2e] px-4 py-3 font-bold text-sm uppercase tracking-wide hover:bg-[#fafafa] transition-all text-center"
                >
                  All Services
                </Link>
              </div>
              
              {/* Contact Us Section */}
              <div className="pt-4 mt-4">
              <div className="inline-block mb-4">
                <h3 className="font-bold text-sm uppercase tracking-wide text-[#1a1a2e] !mb-0">
                  Contact Us
                </h3>
              </div>
              <div className="flex flex-col gap-2">
                <a
                  href="mailto:info@arthandlercollective.org"
                  className="border border-[#e5e5e5] rounded-lg bg-white text-[#1a1a2e] px-4 py-2 font-bold text-sm uppercase tracking-wide hover:bg-[#fafafa] transition-all text-center"
                >
                  info@arthandlercollective.org
                </a>
                <a
                  href="tel:646-741-4047"
                  className="border border-[#e5e5e5] rounded-lg bg-white text-[#1a1a2e] px-4 py-2 font-bold text-sm uppercase tracking-wide hover:bg-[#fafafa] transition-all text-center"
                >
                  646-741-4047
                </a>
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* At a Glance */}
        <div className="border border-[#e5e5e5] rounded-lg shadow-sm bg-white p-4 sm:p-6">
          <div className="inline-block mb-4">
            <h3 className="font-bold text-sm uppercase tracking-wide text-[#1a1a2e] !mb-0">
              At a Glance
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link
              to="/inventory"
              className="border border-[#e5e5e5] rounded-lg bg-white p-4 cursor-pointer hover:bg-[#fafafa] transition-all text-center"
            >
              <div className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e] mb-2">
                AHC Storage Inventory
              </div>
              <div className="text-2xl font-bold text-[#1a1a2e]">
                {storageLoading ? (
                  <span className="animate-pulse">Loading...</span>
                ) : (
                  storageInventory?.meta.totalItems || 0
                )}
              </div>
            </Link>
            <Link
              to="/job"
              className="border border-[#e5e5e5] rounded-lg bg-white p-4 cursor-pointer hover:bg-[#fafafa] transition-all text-center"
            >
              <div className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e] mb-2">
                Upcoming Services
              </div>
              <div className="text-2xl font-bold text-[#1a1a2e]">
                {isLoadingBookings ? (
                  <span className="animate-pulse">Loading...</span>
                ) : (
                  upcomingServices.length
                )}
              </div>
            </Link>
            <Link
              to="/inventory"
              className="border border-[#e5e5e5] rounded-lg bg-white p-4 cursor-pointer hover:bg-[#fafafa] transition-all text-center"
            >
              <div className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e] mb-2">
                Active Inventory
              </div>
              <div className="text-2xl font-bold text-[#1a1a2e]">
                {inventoryLoading ? (
                  <span className="animate-pulse">Loading...</span>
                ) : (
                  <>
                    {inventory?.meta.totalItems || 0}
                    {subclientInventoryCount > 0 && (
                      <span className="italic text-lg ml-2">
                        {subclientInventoryCount}
                      </span>
                    )}
                  </>
                )}
              </div>
            </Link>
          </div>
        </div>

        {/* Shipping Addresses - Collapsible */}
        <div className="border border-[#e5e5e5] rounded-lg shadow-sm bg-white">
          <button
            onClick={() => setIsShippingAddressesExpanded(!isShippingAddressesExpanded)}
            className="w-full p-4 sm:p-6 flex items-center justify-between text-left hover:bg-[#fafafa] transition-colors"
          >
            <div className="inline-block">
              <h3 className="font-bold text-sm uppercase tracking-wide text-[#1a1a2e] !mb-0">
                Shipping Addresses
                {client.shippingAddresses?.length ? (
                  <span className="ml-2 font-mono">({client.shippingAddresses.length})</span>
                ) : null}
              </h3>
            </div>
            {isShippingAddressesExpanded ? (
              <ChevronUp size={20} className="text-[#1a1a2e]" />
            ) : (
              <ChevronDown size={20} className="text-[#1a1a2e]" />
            )}
          </button>
          {isShippingAddressesExpanded && (
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-3 border-t border-[#e5e5e5]">
              {client.shippingAddresses?.length ? (
                client.shippingAddresses.map((address, index) => (
                  <div key={index} className="border border-[#e5e5e5] rounded-lg bg-white p-3 sm:p-4">
                    <p className="font-bold text-sm uppercase text-[#1a1a2e] mb-1">
                      {address.addressName || address.type || "-"}
                    </p>
                    <p className="text-sm font-medium text-[#1a1a2e]">
                      {address.address}
                    </p>
                    <p className="text-sm font-medium text-[#1a1a2e]">
                      {address.city}, {address.state} {address.zip}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm font-medium text-[#1a1a2e]">
                  No shipping addresses available.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Upcoming Services */}
        <div className="border border-[#e5e5e5] rounded-lg shadow-sm bg-white">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="inline-block">
                <h3 className="font-bold text-sm uppercase tracking-wide text-[#1a1a2e] !mb-0">
                  Upcoming Services
                  {upcomingServices.length > 0 && (
                    <span className="ml-2 font-mono">({upcomingServices.length})</span>
                  )}
                </h3>
              </div>
              <div className="flex items-center gap-3">
                <div className="border border-[#e5e5e5] rounded-lg inline-flex overflow-hidden">
                  <button
                    onClick={() => setServicesViewType("list")}
                    className={`px-4 py-2 font-bold text-xs uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
                      servicesViewType === "list"
                        ? "bg-[#1a1a2e] text-white"
                        : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                    }`}
                  >
                    <List size={14} className="inline mr-1" />
                    List
                  </button>
                  <button
                    onClick={() => setServicesViewType("calendar")}
                    className={`px-4 py-2 font-bold text-xs uppercase tracking-wide transition-all ${
                      servicesViewType === "calendar"
                        ? "bg-[#1a1a2e] text-white"
                        : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                    }`}
                  >
                    <Calendar size={14} className="inline mr-1" />
                    Calendar
                  </button>
                </div>
                <Link to="/job" className="text-sm font-bold uppercase text-[#1a1a2e] hover:underline border border-[#e5e5e5] rounded-lg px-3 py-2">
                  View All →
                </Link>
              </div>
            </div>
            {servicesViewType === "list" ? (
              <div className="w-full max-w-full overflow-x-auto min-w-0">
                <Table
                  columns={serviceColumns}
                  dataSource={upcomingServices}
                  loading={isLoadingBookings}
                  rowKey="id"
                  onRow={(record) => ({
                    onClick: () => handleServiceRowClick(record),
                    className: "cursor-pointer hover:bg-[#fafafa] border-b border-[#e5e5e5]",
                  })}
                  pagination={
                    upcomingServices.length > 10
                      ? { pageSize: 10, showSizeChanger: false }
                      : false
                  }
                  scroll={{ x: 800 }}
                  locale={{
                    emptyText: "No upcoming services found",
                  }}
                  size="middle"
                  className="neo-brutalist-table"
                />
              </div>
            ) : (
              <div className="border border-[#e5e5e5] p-4">
                <ServicesCalendarView 
                  services={upcomingServices} 
                  onServiceClick={handleServiceRowClick}
                />
              </div>
            )}
          </div>
        </div>
        </div>
        </div>
      </div>

      {/* Request Service Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full bg-white border-l border-[#e5e5e5] shadow-lg transition-all duration-300 ease-in-out z-50 max-w-[100vw] overflow-visible ${
          isServiceSidebarOpen && !isServiceDetailSidebarOpen ? "w-[420px] translate-x-0" : "w-[420px] translate-x-full"
        }`}
      >
        <RequestServiceSidebar
          open={isServiceSidebarOpen && !isServiceDetailSidebarOpen}
          onClose={() => setIsServiceSidebarOpen(false)}
        />
      </div>

      {/* Service Detail Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full bg-white border-l border-[#e5e5e5] shadow-lg transition-all duration-300 ease-in-out z-50 max-w-[100vw] overflow-x-hidden ${
          isServiceDetailSidebarOpen ? "w-[400px] translate-x-0" : "w-[400px] translate-x-full"
        }`}
      >
        <ServiceDetailSidebar
          service={selectedService}
          booking={selectedBooking}
          onClose={handleCloseServiceSidebar}
        />
      </div>

      {/* Overlay for mobile */}
      {(isServiceSidebarOpen || isServiceDetailSidebarOpen) && (
        <div
          className="fixed inset-0 bg-[#1a1a2e] bg-opacity-25 z-40 lg:hidden"
          onClick={() => {
            setIsServiceSidebarOpen(false);
            handleCloseServiceSidebar();
          }}
        />
      )}
    </div>
  );
}

export default Overview;
